import type {
  JourneyDraftState,
  JourneyResponse,
  LiveTriageDraftState,
  LiveTriageStepSnapshot,
  LiveTriageStepState,
} from '@core/entities/JourneyDraftState';
import type {
  TriageAnswerRecord,
  TriageChoiceOption,
  TriageQuestion,
  TriageQuestionnaire,
  TriageWorkflowStepIdentity,
} from '@core/entities/Triage';
import type { RetryAttemptStore } from '@core/ports/RetryAttemptStore';
import type { UnresolvedSubmissionStore } from '@core/ports/UnresolvedSubmissionStore';

const storageKey = 'inav.dt02.journey-draft';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function createInitialState(recoveryMessage?: string): JourneyDraftState {
  return {
    steps: {},
    recoveryMessage,
  };
}

function cloneSerializable<T>(value: T): T {
  return structuredClone(value);
}

function toStoredQuestionnaire(questionnaire: TriageQuestionnaire): TriageQuestionnaire {
  const { source: _source, ...storedQuestionnaire } = questionnaire;
  return cloneSerializable(storedQuestionnaire);
}

function collectStoredOptions(
  questions: readonly TriageQuestion[],
  collected: Map<string, readonly TriageChoiceOption[]> = new Map(),
): Map<string, readonly TriageChoiceOption[]> {
  for (const question of questions) {
    if (question.options && question.options.length > 0) {
      collected.set(question.linkId, question.options);
    }

    if (question.children) {
      collectStoredOptions(question.children, collected);
    }
  }

  return collected;
}

function mergeOptionsByCode(
  replayedOptions: readonly TriageChoiceOption[],
  storedOptions: readonly TriageChoiceOption[],
): readonly TriageChoiceOption[] {
  const replayedCodes = new Set(replayedOptions.map((option) => option.code));
  return [...replayedOptions, ...storedOptions.filter((option) => !replayedCodes.has(option.code))];
}

function withStoredOptions(
  question: TriageQuestion,
  answers: TriageAnswerRecord,
  storedOptions: ReadonlyMap<string, readonly TriageChoiceOption[]>,
): TriageQuestion {
  const optionsForAnswer = Object.hasOwn(answers, question.linkId) ? storedOptions.get(question.linkId) : undefined;
  return {
    ...question,
    ...(optionsForAnswer ? { options: mergeOptionsByCode(question.options ?? [], optionsForAnswer) } : {}),
    ...(question.children
      ? { children: question.children.map((child) => withStoredOptions(child, answers, storedOptions)) }
      : {}),
  };
}

// A replayed deferred-search step arrives without options, so keep the saved ones that label the restored answers.
function carryForwardAnsweredOptions(
  questionnaire: TriageQuestionnaire,
  existingStep: LiveTriageStepState | undefined,
): TriageQuestionnaire {
  if (!existingStep || Object.keys(existingStep.answers).length === 0) {
    return questionnaire;
  }

  const storedOptions = collectStoredOptions(existingStep.questionnaire.questions);
  return {
    ...questionnaire,
    questions: questionnaire.questions.map((question) => withStoredOptions(question, existingStep.answers, storedOptions)),
  };
}

function cloneState(state: JourneyDraftState): JourneyDraftState {
  return {
    recoveryMessage: state.recoveryMessage,
    retryUsed: state.retryUsed,
    submissionOutcomeUnknown: state.submissionOutcomeUnknown,
    steps: Object.fromEntries(
      Object.entries(state.steps).map(([path, step]) => [path, {
        previousPath: step.previousPath,
        response: step.response
          ? {
            question: step.response.question,
            answer: step.response.answer,
            value: step.response.value,
          }
          : undefined,
      }])
    ),
    liveTriage: state.liveTriage
      ? {
        activeStepId: state.liveTriage.activeStepId,
        steps: Object.fromEntries(
          Object.entries(state.liveTriage.steps).map(([stepId, step]) => [stepId, {
            previousStepId: step.previousStepId,
            questionnaire: toStoredQuestionnaire(step.questionnaire),
            answers: cloneSerializable(step.answers),
          } satisfies LiveTriageStepState])
        ),
      }
      : undefined,
  };
}

function isLiveTriageDraftState(value: unknown): value is LiveTriageDraftState {
  return isRecord(value) && isRecord(value.steps);
}

function isPositiveInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

function hasValidWorkflowStepIdentity(questionnaire: TriageQuestionnaire): questionnaire is TriageQuestionnaire & { workflowStepIdentity: TriageWorkflowStepIdentity } {
  const workflowStepIdentity = questionnaire.workflowStepIdentity;
  return isRecord(workflowStepIdentity)
    && isPositiveInteger(workflowStepIdentity.journeyRevision)
    && isPositiveInteger(workflowStepIdentity.stepSequence)
    && isPositiveInteger(workflowStepIdentity.questionnaireEventSequence);
}

export class JourneyDraftStore implements RetryAttemptStore, UnresolvedSubmissionStore {
  private state = this.hydrate();

  public clear(): void {
    this.state = createInitialState();
    this.persist();
  }

  public clearLiveTriageState(recoveryMessage?: string): void {
    this.state = {
      ...this.state,
      liveTriage: undefined,
      recoveryMessage,
    };
    this.persist();
  }

  public getSnapshot(): JourneyDraftState {
    return cloneState(this.state);
  }

  public consumeRecoveryMessage(): string | undefined {
    const { recoveryMessage } = this.state;
    if (!recoveryMessage) {
      return undefined;
    }

    this.state = {
      ...this.state,
      recoveryMessage: undefined,
    };
    this.persist();
    return recoveryMessage;
  }

  public hasUsedRetry(): boolean {
    return this.state.retryUsed === true;
  }

  public markRetryUsed(): void {
    this.state = { ...this.state, retryUsed: true };
    this.persist();
  }

  public clearRetryUsed(): void {
    if (!this.state.retryUsed) {
      return;
    }

    this.state = { ...this.state, retryUsed: undefined };
    this.persist();
  }

  public hasUnknownSubmissionOutcome(): boolean {
    return this.state.submissionOutcomeUnknown === true;
  }

  public markUnknownSubmissionOutcome(): void {
    this.state = {
      ...this.state,
      submissionOutcomeUnknown: true,
      liveTriage: undefined,
    };
    this.persist();
  }

  public clearUnknownSubmissionOutcome(): void {
    if (!this.state.submissionOutcomeUnknown) {
      return;
    }

    this.state = { ...this.state, submissionOutcomeUnknown: undefined };
    this.persist();
  }

  public getPreviousPath(path: string): string | undefined {
    return this.state.steps[path]?.previousPath;
  }

  public getResponse(path: string): JourneyResponse | undefined {
    return this.state.steps[path]?.response;
  }

  public getActiveLiveTriageStep(): LiveTriageStepSnapshot | undefined {
    const activeStepId = this.state.liveTriage?.activeStepId;
    if (!activeStepId) {
      return undefined;
    }

    const step = this.state.liveTriage?.steps[activeStepId];
    if (!this.isValidLiveTriageStep(step)) {
      this.markCorruptedState();
      return undefined;
    }

    return {
      stepId: activeStepId,
      previousStepId: step.previousStepId,
      questionnaire: cloneSerializable(step.questionnaire),
      answers: cloneSerializable(step.answers),
    };
  }

  public initializeLiveTriageStep(stepId: string, questionnaire: TriageQuestionnaire): void {
    this.state = {
      ...this.state,
      liveTriage: {
        activeStepId: stepId,
        steps: {
          [stepId]: {
            questionnaire: toStoredQuestionnaire(questionnaire),
            answers: {},
          },
        },
      },
    };

    this.persist();
  }

  /** Returns the stored questionnaire, keeping saved options that label any restored answers. */
  public advanceLiveTriageStep(currentStepId: string, nextStepId: string, questionnaire: TriageQuestionnaire): TriageQuestionnaire {
    const currentStep = this.state.liveTriage?.steps[currentStepId];
    const liveTriageSteps = this.state.liveTriage?.steps;
    const existingNextStep = this.state.liveTriage?.steps[nextStepId];
    const nextAnswers = existingNextStep?.answers ?? {};
    const restoredQuestionnaire = carryForwardAnsweredOptions(questionnaire, existingNextStep);
    const storedQuestionnaire = toStoredQuestionnaire(restoredQuestionnaire);

    if (currentStepId === nextStepId && currentStep) {
      this.state = {
        ...this.state,
        liveTriage: {
          activeStepId: nextStepId,
          steps: {
            ...liveTriageSteps,
            [nextStepId]: {
              ...currentStep,
              questionnaire: storedQuestionnaire,
              answers: cloneSerializable(nextAnswers),
            },
          },
        },
      };

      this.persist();
      return restoredQuestionnaire;
    }

    this.state = {
      ...this.state,
      liveTriage: {
        activeStepId: nextStepId,
        steps: {
          ...liveTriageSteps,
          ...(currentStep
            ? {
              [currentStepId]: {
                ...currentStep,
              },
            }
            : {}),
          [nextStepId]: {
            previousStepId: currentStepId,
            questionnaire: storedQuestionnaire,
            answers: cloneSerializable(nextAnswers),
          },
        },
      },
    };

    this.persist();
    return restoredQuestionnaire;
  }

  public advanceLiveTriageStepAfterRevisionChange(
    currentStepId: string,
    nextStepId: string,
    questionnaire: TriageQuestionnaire,
  ): void {
    const existingSteps = this.state.liveTriage?.steps;
    const lineage = this.getLineageToStep(currentStepId);
    if (!existingSteps || lineage.length === 0) {
      this.initializeLiveTriageStep(nextStepId, questionnaire);
      return;
    }

    const prunedSteps: LiveTriageDraftState['steps'] = Object.fromEntries(
      lineage
        .filter((stepId) => existingSteps[stepId] !== undefined)
        .map((stepId) => [stepId, existingSteps[stepId]]),
    );

    const currentStep = prunedSteps[currentStepId];
    if (!currentStep) {
      this.initializeLiveTriageStep(nextStepId, questionnaire);
      return;
    }

    if (currentStepId === nextStepId) {
      this.state = {
        ...this.state,
        liveTriage: {
          activeStepId: nextStepId,
          steps: {
            ...prunedSteps,
            [nextStepId]: {
              ...currentStep,
              questionnaire: toStoredQuestionnaire(questionnaire),
            },
          },
        },
      };

      this.persist();
      return;
    }

    this.state = {
      ...this.state,
      liveTriage: {
        activeStepId: nextStepId,
        steps: {
          ...prunedSteps,
          [nextStepId]: {
            previousStepId: currentStepId,
            questionnaire: toStoredQuestionnaire(questionnaire),
            answers: {},
          },
        },
      },
    };

    this.persist();
  }

  public getLiveTriageAnswers(stepId: string): TriageAnswerRecord {
    const step = this.state.liveTriage?.steps[stepId];
    if (!this.isValidLiveTriageStep(step)) {
      return {};
    }

    return cloneSerializable(step.answers);
  }

  public hasLiveTriageStep(stepId: string): boolean {
    return this.isValidLiveTriageStep(this.state.liveTriage?.steps[stepId]);
  }

  public recordLiveTriageAnswers(stepId: string, answers: TriageAnswerRecord): void {
    const step = this.state.liveTriage?.steps[stepId];
    if (!this.isValidLiveTriageStep(step)) {
      return;
    }

    this.state = {
      ...this.state,
      liveTriage: {
        activeStepId: this.state.liveTriage?.activeStepId,
        steps: {
          ...this.state.liveTriage?.steps,
          [stepId]: {
            ...step,
            answers: cloneSerializable(answers),
          },
        },
      },
    };

    this.persist();
  }

  public getAccumulatedLiveTriageAnswers(stepId: string): TriageAnswerRecord {
    const liveTriage = this.state.liveTriage;
    if (!liveTriage) {
      return {};
    }

    const liveTriageSteps: LiveTriageDraftState['steps'] = liveTriage.steps;

    const stepIds: string[] = [];
    let currentStepId: string | undefined = stepId;

    while (currentStepId) {
      const step: LiveTriageStepState | undefined = liveTriageSteps[currentStepId];
      if (!this.isValidLiveTriageStep(step)) {
        return {};
      }

      stepIds.unshift(currentStepId);
      currentStepId = step.previousStepId;
    }

    return stepIds.reduce<TriageAnswerRecord>((answers, currentId) => ({
      ...answers,
      ...cloneSerializable(liveTriageSteps[currentId].answers),
    }), {});
  }

  public hasLiveTriageState(): boolean {
    return this.state.liveTriage !== undefined;
  }

  public canMoveToPreviousLiveTriageStep(): boolean {
    return Boolean(this.getActiveLiveTriageStep()?.previousStepId);
  }

  public moveToPreviousLiveTriageStep(): boolean {
    const activeStep = this.getActiveLiveTriageStep();
    if (!activeStep?.previousStepId) {
      return false;
    }

    this.state = {
      ...this.state,
      liveTriage: {
        ...(this.state.liveTriage ?? { steps: {} }),
        activeStepId: activeStep.previousStepId,
      },
    };

    this.persist();
    return true;
  }

  public recordResponse(path: string, response: JourneyResponse): void {
    this.state = {
      ...this.state,
      steps: {
        ...this.state.steps,
        [path]: {
          ...this.state.steps[path],
          response,
        },
      },
    };

    this.persist();
  }

  public markCorruptedState(): void {
    this.state = createInitialState('We restarted this journey because your previous answers could not be restored safely.');
    this.persist();
  }

  public restore(snapshot: JourneyDraftState): void {
    this.state = cloneState(snapshot);
    this.persist();
  }

  public recordForwardTransition(currentPath: string, nextPath: string, response?: JourneyResponse): void {
    this.state = {
      ...this.state,
      steps: {
        ...this.state.steps,
        [currentPath]: {
          ...this.state.steps[currentPath],
          response: response ?? this.state.steps[currentPath]?.response,
        },
        [nextPath]: {
          ...this.state.steps[nextPath],
          previousPath: currentPath,
        },
      },
    };

    this.persist();
  }

  private hydrate(): JourneyDraftState {
    try {
      const stored = globalThis.sessionStorage.getItem(storageKey);
      if (!stored) {
        return createInitialState();
      }

      const parsed = JSON.parse(stored) as unknown;
      if (!isRecord(parsed) || !isRecord(parsed.steps)) {
        return createInitialState('We restarted this journey because your previous answers could not be restored safely.');
      }

      if (parsed.liveTriage !== undefined && !isLiveTriageDraftState(parsed.liveTriage)) {
        return createInitialState('We restarted this journey because your previous answers could not be restored safely.');
      }

      const submissionOutcomeUnknown = parsed.submissionOutcomeUnknown === true;
      return {
        recoveryMessage: typeof parsed.recoveryMessage === 'string' ? parsed.recoveryMessage : undefined,
        retryUsed: parsed.retryUsed === true ? true : undefined,
        submissionOutcomeUnknown: submissionOutcomeUnknown ? true : undefined,
        steps: parsed.steps as JourneyDraftState['steps'],
        liveTriage: submissionOutcomeUnknown ? undefined : parsed.liveTriage,
      };
    } catch {
      return createInitialState('We restarted this journey because your previous answers could not be restored safely.');
    }
  }

  private isValidLiveTriageStep(step: LiveTriageStepState | undefined): step is LiveTriageStepState {
    return isRecord(step)
      && isRecord(step.questionnaire)
      && Array.isArray(step.questionnaire.questions)
      && hasValidWorkflowStepIdentity(step.questionnaire)
      && isRecord(step.answers);
  }

  // Returns true if making nextStepId the new active step with currentStepId as its
  // previousStepId would introduce a cycle. This happens when nextStepId is already
  // present anywhere in the current ancestry chain of currentStepId (i.e. nextStepId
  // is an ancestor of currentStepId), meaning linking currentStepId → nextStepId
  // would form a loop. The controller can use this to decide whether to call
  // initializeLiveTriageStep (fresh start) vs advanceLiveTriageStep (continuation).
    public wouldAdvanceCreateCycle(currentStepId: string, nextStepId: string): boolean {
      const steps = this.state.liveTriage?.steps;
      if (!steps) {
        return false;
      }

      const visited = new Set<string>();
      let ancestorId: string | undefined = currentStepId;

    while (ancestorId) {
      if (visited.has(ancestorId)) {
        // Existing cycle in the chain — treat as would-create-cycle to be safe.
        return true;
      }

      visited.add(ancestorId);

      if (ancestorId === nextStepId) {
        return true;
      }

      ancestorId = steps[ancestorId]?.previousStepId;
    }

    return false;
  }

  private getLineageToStep(stepId: string): readonly string[] {
    const steps = this.state.liveTriage?.steps;
    if (!steps || !steps[stepId]) {
      return [];
    }

    const lineage: string[] = [];
    const visited = new Set<string>();
    let currentStepId: string | undefined = stepId;

    while (currentStepId) {
      if (visited.has(currentStepId)) {
        return [];
      }

      visited.add(currentStepId);
      lineage.unshift(currentStepId);
      currentStepId = steps[currentStepId]?.previousStepId;
    }

    return lineage;
  }

  private persist(): void {
    globalThis.sessionStorage.setItem(storageKey, JSON.stringify(this.state));
  }
}